// Aerodynamic and powerplant helpers for the aircraft tick: drag polar, lapse laws, propeller efficiency,
// stall and best-L/D speeds, and the two speed solvers. Pure functions, SI units.
import { G, RHO0, densityRatio, speedOfSound, isaDensity, isaTemperatureK } from './atmosphere';
import type { AirEff } from './resolve';
import { maxMassKg } from './resolve';

export interface DragOpts {
  /** Extra zero-lift drag (external stores) still attached. */
  cd0Extra: number;
  /** Weather-cell push-through penalty, e.g. 0.15. */
  dragPenalty: number;
}

/** Drag (N) from the parabolic polar plus a quartic wave-drag rise above machCrit (estimate: textbook form). */
export function dragN(eff: AirEff, massKg: number, rho: number, a: number, vMs: number, o: DragOpts): number {
  const q = 0.5 * rho * vMs * vMs;
  const cl = (massKg * G) / (q * eff.wingAreaM2);
  const mach = vMs / a;
  const wave = mach > eff.machCrit ? 20 * Math.pow(mach - eff.machCrit, 4) : 0;
  const cd = eff.cd0 + o.cd0Extra + wave + (eff.inducedMul * cl * cl) / (Math.PI * eff.aspectRatio * eff.oswald);
  return q * eff.wingAreaM2 * cd * (1 + o.dragPenalty);
}

/** Naturally aspirated piston lapse (spec §7): max(0, σ − (1 − σ)/7.55). */
export function naLapse(sigma: number): number {
  return Math.max(0, sigma - (1 - sigma) / 7.55);
}

/** Shaft power available from all engines at full throttle (W). */
export function shaftPowerAvailableW(eff: AirEff, kind: 'piston' | 'turboprop', altM: number): number {
  const sigma = densityRatio(altM);
  const p0 = eff.ratedPowerKw * 1000 * eff.engineCount;
  if (eff.criticalAltM <= 0) return p0 * (kind === 'piston' ? naLapse(sigma) : Math.pow(sigma, 0.7));
  const sigmaCrit = densityRatio(eff.criticalAltM);
  if (sigma >= sigmaCrit) return p0;
  const rel = sigma / sigmaCrit;
  // Turbo pistons lapse like a naturally aspirated engine relative to the critical altitude;
  // flat-rated turboprops lapse as σ^0.7 (estimate: turbine thrust/power lapse exponent of spec §7).
  return p0 * (kind === 'piston' ? naLapse(rel) : Math.pow(rel, 0.7));
}

/**
 * Thrust available from all engines (N). σ^0.7 per spec §7; civil high-bypass turbofans additionally lose
 * thrust with Mach as (1 − 0.49·√M) — estimate: Mattingly, Aircraft Engine Design, high-bypass lapse model.
 */
export function thrustAvailableN(eff: AirEff, altM: number, mach: number, afterburner: boolean): number {
  const sigma = densityRatio(altM);
  const t0 = (afterburner && eff.afterburnerThrustN > 0 ? eff.afterburnerThrustN : eff.ratedThrustN) * eff.engineCount;
  const machLapse = eff.afterburnerThrustN > 0 ? 1 : Math.max(0.3, 1 - 0.49 * Math.sqrt(Math.max(0, mach)));
  return t0 * Math.pow(sigma, 0.7) * machLapse;
}

/**
 * Civil high-bypass TSFC relative to the published cruise value (M 0.84, 35,000 ft): (0.464 + 0.638·M)·√(T/218.8 K).
 * estimate: the linear-in-Mach, √θ form calibrated on the GE90's published take-off TSFC 0.278 and cruise 0.520 lb/lbf·h,
 * so climb fuel is not charged at the cruise figure. Military low-bypass engines keep their flat cruise value.
 */
export function tsfcFactor(eff: AirEff, altM: number, mach: number): number {
  if (eff.afterburnerThrustN > 0) return 1;
  return (0.464 + 0.638 * Math.max(0, mach)) * Math.sqrt(isaTemperatureK(altM) / 218.8);
}

/** Propeller efficiency (spec §7): constant-speed 0.85; fixed pitch 0.82 − 0.5·((V − Vd)/Vd)², floor 0.5. */
export function propEfficiency(eff: AirEff, vMs: number): number {
  const base = eff.propeller === 'constant-speed'
    ? 0.85
    : Math.max(0.5, 0.82 - 0.5 * Math.pow((vMs - eff.vDesignMs) / eff.vDesignMs, 2));
  return Math.min(0.92, base * eff.propEffMul);
}

/** Clean stall TAS at this mass and density (vStallMs is quoted at maximum mass, sea level). */
export function vStallMs(eff: AirEff, massKg: number, rho: number): number {
  return eff.vStallMs * Math.sqrt((massKg / maxMassKg(eff)) * (RHO0 / rho));
}

/** Best lift-to-drag TAS for the parabolic polar. */
export function vBestLdMs(eff: AirEff, massKg: number, rho: number, cd0Extra: number): number {
  const k = eff.inducedMul / (Math.PI * eff.aspectRatio * eff.oswald);
  return Math.sqrt((2 * massKg * G) / (rho * eff.wingAreaM2)) * Math.pow(k / (eff.cd0 + cd0Extra), 0.25);
}

function bisectDecreasing(f: (v: number) => number, lo: number, hi: number): number | null {
  // f is positive at lo and negative at hi on the branch we want; returns the root.
  let flo = f(lo);
  if (flo < 0) return null;
  if (f(hi) > 0) return hi;
  for (let i = 0; i < 48; i++) {
    const mid = 0.5 * (lo + hi);
    const fm = f(mid);
    if (fm > 0) { lo = mid; flo = fm; } else hi = mid;
  }
  return 0.5 * (lo + hi);
}

/** Cruise TAS (m/s) at which η_p(V)·P_shaft = D(V)·V, high-speed root; null if the power cannot hold level flight. */
export function solvePropSpeed(eff: AirEff, massKg: number, altM: number, pShaftW: number, o: DragOpts): number | null {
  const rho = isaDensity(altM), a = speedOfSound(altM);
  const lo = Math.max(1.1 * vStallMs(eff, massKg, rho), 0.95 * vBestLdMs(eff, massKg, rho, o.cd0Extra));
  const hi = Math.min(400, eff.machMax * a);
  if (hi <= lo) return null;
  const f = (v: number): number => propEfficiency(eff, v) * pShaftW - dragN(eff, massKg, rho, a, v, o) * v;
  const root = bisectDecreasing(f, lo, hi);
  if (root !== null) return root;
  // Below best L/D the power-required curve rises again towards the stall; try the slow branch once.
  const slowLo = 1.1 * vStallMs(eff, massKg, rho);
  if (slowLo < lo && f(slowLo) >= 0) return lo;
  return null;
}

/** Cruise TAS (m/s) at which the commanded thrust equals drag, high-speed root; null if thrust cannot hold level flight. */
export function solveJetSpeed(
  eff: AirEff, massKg: number, altM: number, thrustAt: (mach: number) => number, o: DragOpts,
): number | null {
  const rho = isaDensity(altM), a = speedOfSound(altM);
  const lo = Math.max(1.1 * vStallMs(eff, massKg, rho), vBestLdMs(eff, massKg, rho, o.cd0Extra));
  const hi = Math.min(600, eff.machMax * a);
  if (hi <= lo) return null;
  const f = (v: number): number => thrustAt(v / a) - dragN(eff, massKg, rho, a, v, o);
  return bisectDecreasing(f, lo, hi);
}
